import { useCallback } from "react";
import { useCreate, useInvalidate, useList } from "@refinedev/core";
import { useSession } from "hooks/useSession";
import type { BundleRequest, BundleResponse } from "@/types";

const ACTIVE_STATUSES = new Set(["Pending", "Running"]);

export const exportsResource = (orgId: string) =>
  `api/organizations/${orgId}/exports/data-bundle`;

export const useDataExports = () => {
  const { currentOrganization } = useSession();
  const orgId = currentOrganization?.id;
  const resource = orgId ? exportsResource(orgId) : "";

  const list = useList<BundleResponse>({
    resource,
    queryOptions: {
      enabled: Boolean(orgId),
    },
    pagination: { mode: "off" },
    errorNotification: false,
  });

  const { mutateAsync: createBundle, isPending: isCreating } =
    useCreate<BundleResponse>();
  const invalidate = useInvalidate();

  const findActive = useCallback(
    (rows?: BundleResponse[]) =>
      rows?.find((row) => ACTIVE_STATUSES.has(row.status)),
    []
  );

  const triggerExport = useCallback(
    async (values: BundleRequest) => {
      if (!orgId) {
        throw new Error("No active organization");
      }
      try {
        const response = await createBundle({
          resource,
          values,
          successNotification: false,
          errorNotification: false,
        });
        await invalidate({ resource, invalidates: ["list"] });
        return { bundle: response.data, alreadyInProgress: false as const };
      } catch (error) {
        const statusCode = (error as { statusCode?: number }).statusCode;
        if (statusCode === 409) {
          const refreshed = await list.refetch();
          const existing = findActive(refreshed.data?.data);
          return { bundle: existing, alreadyInProgress: true as const };
        }
        throw error;
      }
    },
    [createBundle, findActive, invalidate, list, orgId, resource]
  );

  return {
    bundles: list.data?.data ?? [],
    isLoading: list.isLoading,
    error: list.error,
    refetch: list.refetch,
    activeBundle: findActive(list.data?.data),
    triggerExport,
    isCreating,
    orgId,
  };
};
